import React, { useRef, useState, useCallback } from 'react';
import { UserOutlined, WarningOutlined } from '@ant-design/icons';
import { prop, propOr, equals, map, path, filter, gt, propEq, length, slice, toLower, trim, includes } from 'ramda';
import { isNotNil, isNotNilOrEmpty } from 'ramda-adjunct';
import moment from 'moment';
import { ENTRY_KIND, ACTIONS, TARGET_TYPES } from '../../../../../shared/documentHistoriesConstants';
import { DOCUMENT_HISTORY_ACTION_LABELS,
  DOCUMENT_HISTORY_BADGE_COLORS } from '../../../../_CONST';
import RichTooltip from '../../../../components/RichTooltip';
import withUser from '../../../../withUser';
import HighlightText from './HighlightText';
import TruncatedHighlightText from './TruncatedHighlightText';

const getTypeBadge = (targetType) => {
  const typeLabels = {
    [prop('WIDGET', TARGET_TYPES)]: 'Widget',
    [prop('SECTION', TARGET_TYPES)]: 'Section',
    [prop('DOCUMENT', TARGET_TYPES)]: 'Document'
  };

  return {
    label: propOr(targetType, targetType, typeLabels),
    colorClasses: propOr(DOCUMENT_HISTORY_BADGE_COLORS.DOCUMENT, targetType, DOCUMENT_HISTORY_BADGE_COLORS)
  };
};

const getMetaBadgesExt = (isExternal) => {
  return isExternal
    ? [{
      label: 'Modif. ext.',
      colorClasses: DOCUMENT_HISTORY_BADGE_COLORS.WIDGET_EXTERNAL
    }]
    : [];
};

const getMetaBadgesPlacement = (placementsCount) => {
  const hasPlacements = isNotNil(placementsCount) && gt(placementsCount, 0);
  return hasPlacements
    ? [{
      label: `${placementsCount} emplacement${gt(placementsCount, 1) ? 's' : ''}`,
      colorClasses: DOCUMENT_HISTORY_BADGE_COLORS.WIDGET_PLACEMENTS
    }]
    : [];
};

const formatActionLabel = (userIsSuperAdmin, action, targetName, sourceOfferLabel, previousName, targetType) => {
  if (equals(action, prop('DUPLICATED_FROM', ACTIONS))) {
    return userIsSuperAdmin
      ? (sourceOfferLabel ? `Dupliqué depuis "${sourceOfferLabel}"` : 'Dupliqué depuis une offre supprimée')
      : `${DOCUMENT_HISTORY_ACTION_LABELS.CREATION} du document`;
  }
  const label = propOr(action, action, DOCUMENT_HISTORY_ACTION_LABELS);
  if ((equals(action, prop('CREATION', ACTIONS)) || equals(action, prop('COMPOSITION_UPDATE', ACTIONS))) && equals(targetType, prop('DOCUMENT', TARGET_TYPES))) {
    return `${label} du document`;
  }
  const base = `${label} : "${targetName}"`;
  if (equals(action, prop('NAME_UPDATE', ACTIONS)) && isNotNilOrEmpty(previousName)) {
    return `${base} anciennement "${previousName}"`;
  }
  return base;
};

const getLastNameInitial = (lastName) => {
  if (!isNotNilOrEmpty(lastName)) return '';
  return `${lastName.charAt(0).toUpperCase()}.`;
};

const MAX_AUTHOR_DISPLAY_LENGTH = 10;

const formatAuthor = (author) => {
  const firstName = propOr('', 'firstName', author);
  const lastName = prop('lastName', author);
  const initial = isNotNilOrEmpty(lastName) ? `${lastName.charAt(0).toUpperCase()}.` : '';
  const authorLabel = initial ? `${firstName} ${initial}` : firstName;
  const overflowIsOnlyInitialDot = length(authorLabel) === MAX_AUTHOR_DISPLAY_LENGTH + 1;
  if (length(authorLabel) <= MAX_AUTHOR_DISPLAY_LENGTH || overflowIsOnlyInitialDot) return authorLabel;
  return `${slice(0, MAX_AUTHOR_DISPLAY_LENGTH, authorLabel)}...`;
};

const getAuthorHighlightTerm = (author, searchTerm) => {
  const lastName = prop('lastName', author);
  const normalizedSearchTerm = toLower(trim(searchTerm || ''));
  const normalizedLastName = toLower(trim(lastName || ''));

  if (!normalizedSearchTerm || !normalizedLastName) return searchTerm;

  const matchesLastName = includes(normalizedSearchTerm, normalizedLastName);

  if (!matchesLastName) return searchTerm;

  return getLastNameInitial(lastName);
};

const buildAuthorTooltipTitle = (author) => {
  const firstName = propOr('', 'firstName', author);
  const lastName = prop('lastName', author);
  if (!isNotNilOrEmpty(lastName)) return firstName || null;
  return `${firstName} ${lastName}`;
};

const buildActionTooltipTitle = (targetName, typeBadge, isNameUpdate, previousName) => {
  const colorClass = prop('text', typeBadge.colorClasses);
  return (
    <div className="flex flex-col gap-1">
      <span className={`font-bold ${colorClass}`}>Nom du {prop('label', typeBadge)} :</span>
      {targetName}{isNameUpdate && isNotNilOrEmpty(previousName) ? ` anciennement ${previousName}` : ''}
    </div>
  );
};

const buildExternalTooltipTitle = (sourceDocumentName) => (
  <div className="flex flex-col gap-1">
    <span className="font-bold text-flexi-history-error">Widget modifié depuis le document :</span>
    {sourceDocumentName}
  </div>
);

const buildPlacementsTooltipTitle = (placements) => {
  if (!isNotNilOrEmpty(placements)) return null;
  const nonOriginPlacements = filter(propEq(false, 'isOrigin'), placements);
  if (!isNotNilOrEmpty(nonOriginPlacements)) return null;
  return (
    <span>
      <span className="font-bold text-flexi-history-placement">Ce widget est également présent dans :</span>
      <ul>
        {map(({ locationLabel }) => (
          <li key={locationLabel}>{locationLabel}</li>
        ), nonOriginPlacements)}
      </ul>
    </span>
  );
};

const renderMetaBadges = (badges, iconNode) => map(({ label, colorClasses }) => (
  <span
    key={label}
    className={`
      inline-flex items-center px-3 py-1 w-fit shrink-0
      rounded-full text-xs font-semibold
      ${prop('text', colorClasses)}
      ${prop('bg', colorClasses)}
    `}
  >
    {iconNode} {label}
  </span>
))(badges);

const DocumentHistoryEntry = ({ entry, searchTerm, userIsSuperAdmin }) => {
  const actionCellRef = useRef(null);
  const locationRef = useRef(null);
  const entryKind = prop('entryKind', entry);
  const [isActionTruncated, setIsActionTruncated] = useState(false);
  const handleTruncationChange = useCallback((value) => setIsActionTruncated(value), []);

  if (equals(entryKind, prop('MILESTONE', ENTRY_KIND))) {
    return (
      <div className="flex items-center justify-start py-2 px-4">
        <span className="text-flexi-history-milestone text-sm">
          <span className="mr-1">ⓘ</span>
          {prop('milestoneLabel', entry)}
        </span>
      </div>
    );
  }

  const savedAt = prop('savedAt', entry);
  const action = prop('action', entry);
  const targetType = prop('targetType', entry);
  const targetName = prop('targetName', entry);
  const previousName = prop('previousName', entry);
  const sourceOfferLabel = prop('sourceOfferLabel', entry);
  const isExternal = propOr(false, 'isExternal', entry);
  const placementsCount = prop('placementsCount', entry);
  const locationLabel = prop('locationLabel', entry);
  const author = prop('author', entry);
  const placements = path(['widget', 'placements'], entry);
  const sourceDocumentName = path(['widget', 'sourceDocumentName'], entry);
  const typeBadge = getTypeBadge(targetType);
  const metaExtBadges = getMetaBadgesExt(isExternal);
  const metaPlacementBadges = getMetaBadgesPlacement(placementsCount);
  const timeLabel = moment(savedAt).format('HH:mm:ss');
  const authorLabel = formatAuthor(author);
  const authorHighlightTerm = getAuthorHighlightTerm(author, searchTerm);

  const actionText = formatActionLabel(userIsSuperAdmin, action, targetName, sourceOfferLabel, previousName, targetType);
  const isNameUpdate = equals(action, prop('NAME_UPDATE', ACTIONS));
  const actionTooltipTitle = buildActionTooltipTitle(targetName, typeBadge, isNameUpdate, previousName);
  const externalTooltipTitle = buildExternalTooltipTitle(sourceDocumentName);
  const placementsTooltipTitle = buildPlacementsTooltipTitle(placements);
  const authorTooltipTitle = buildAuthorTooltipTitle(author);

  return (
    <div className="grid grid-cols-[80px_80px_1fr_110px_140px_140px] items-center gap-3 py-2.5 hover:bg-gray-50 rounded-lg px-4">
      <span className="font-mono text-sm text-flexi-grey">
        {timeLabel}
      </span>

      <span
        className={`
          inline-flex items-center px-2 py-0.5 w-fit
          rounded-full border text-xs font-semibold
          ${prop('text', typeBadge.colorClasses)}
          ${prop('bg', typeBadge.colorClasses)}
          ${prop('border', typeBadge.colorClasses)}
        `}
      >
        {prop('label', typeBadge)}
      </span>

      <RichTooltip title={isActionTruncated ? actionTooltipTitle : null} innerStyle={{ minWidth: 450 }}>
        <span ref={actionCellRef} className="text-sm text-gray-800 min-w-0 flex items-center overflow-hidden">
          <TruncatedHighlightText
            text={actionText}
            searchTerm={searchTerm}
            actionCellRef={actionCellRef}
            locationRef={locationRef}
            onTruncationChange={handleTruncationChange}
          />
          {isNotNilOrEmpty(locationLabel) && (
            <HighlightText
              ref={locationRef}
              text={`(${locationLabel})`}
              searchTerm={searchTerm}
              className="italic text-flexi-grey ml-1 shrink-0 whitespace-nowrap"
            />
          )}
        </span>
      </RichTooltip>

      <div>
        {isNotNilOrEmpty(metaExtBadges)
          && (
            <RichTooltip title={externalTooltipTitle}>
              <span>
                {renderMetaBadges(metaExtBadges, <WarningOutlined className="mr-2" />)}
              </span>
            </RichTooltip>
          )}
      </div>

      <div>
        {isNotNilOrEmpty(metaPlacementBadges)
          && (
            <RichTooltip title={placementsTooltipTitle}>
              <span>
                {renderMetaBadges(
                  metaPlacementBadges,
                  <img src="/icons/icon-copy.png" alt="copy" className="w-3 h-3 object-contain mr-2" />
                )}
              </span>
            </RichTooltip>
          )}
      </div>

      <RichTooltip title={authorTooltipTitle}>
        <span className="flex items-center gap-1 shrink-0">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200">
            <UserOutlined className="!text-flexi-grey" />
          </span>
          <HighlightText text={authorLabel} searchTerm={authorHighlightTerm} />
        </span>
      </RichTooltip>
    </div>
  );
};

export default withUser(DocumentHistoryEntry);
